"use client"

import Box from "@mui/material/Box"
import Icons from "@src/components/Icons"
import ProductCard from "@src/components/ProductCard"
import { useCallback, useEffect, useRef, useState } from "react"
import type SwiperInstance from "swiper"
import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/pagination"

const ButtonNavigation = ({ children, onClick, disabled }) => {
  return (
    <Box
      sx={{
        backgroundColor: "#7D7D7D",
        width: {
          xs: "24px",
          sm: "32px",
          lg: "48px"
        },
        height: {
          xs: "24px",
          sm: "32px",
          lg: "48px"
        },
        borderRadius: "50%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        cursor: "pointer",
        svg: {
          color: "white"
        },
        "&.disabled": {
          backgroundColor: "#ECECEC",
          cursor: "not-allowed",
          svg: {
            color: "#B9B9B9"
          }
        }
      }}
      onClick={onClick}
      className={disabled ? "disabled" : ""}
    >
      {children}
    </Box>
  )
}

const CarouselProductSale = props => {
  const { data = [] } = props || {}

  const containerRef = useRef<HTMLDivElement>(null)
  const sliderRef = useRef<SwiperInstance | null>(null)
  const [initialized, setInitialized] = useState(false)
  const [isEnd, setIsEnd] = useState(false)
  const [isBeginning, setIsBeginning] = useState(true)

  useEffect(() => {
    const element = containerRef.current
    if (!element) return
    let cancelled = false
    let started = false
    setInitialized(false)

    const initialize = async () => {
      if (started) return
      started = true
      const { default: Swiper } = await import("swiper")
      if (cancelled) return
      const updateNavigation = (swiper: SwiperInstance) => {
        setIsEnd(swiper.isEnd)
        setIsBeginning(swiper.isBeginning)
      }
      sliderRef.current = new Swiper(element, {
        slidesPerView: 2,
        breakpoints: { 200: { slidesPerView: 2 }, 1024: { slidesPerView: 4 } },
        spaceBetween: 24,
        loop: false,
        on: {
          init: updateNavigation,
          slideChange: updateNavigation,
          resize: updateNavigation
        }
      })
      setInitialized(true)
    }

    const observer =
      typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(
            entries => {
              if (entries.some(entry => entry.isIntersecting)) {
                observer?.disconnect()
                void initialize().catch(() => {
                  started = false
                })
              }
            },
            { rootMargin: "600px" }
          )
        : null
    if (observer) observer.observe(element)
    else
      void initialize().catch(() => {
        started = false
      })
    return () => {
      cancelled = true
      observer?.disconnect()
      sliderRef.current?.destroy(true, true)
      sliderRef.current = null
    }
  }, [data])

  const handlePrev = useCallback(() => {
    if (sliderRef.current) {
      sliderRef.current.slidePrev()
    }
  }, [])

  const handleNext = useCallback(() => {
    if (sliderRef.current) {
      sliderRef.current.slideNext()
    }
  }, [])

  return (
    <Box position="relative" width="100%" margin="auto">
      <Box
        sx={{
          minHeight: "318px",
          margin: "auto"
        }}
        position="relative"
      >
        <Box
          ref={containerRef}
          className="swiper mySwiper"
          sx={{
            overflow: initialized ? "hidden" : "auto",
            "& .swiper-slide": {
              width: "calc((100% - 24px) / 2)",
              height: "auto",
              "@media (min-width: 1024px)": { width: "calc((100% - 72px) / 4)" }
            },
            "& .swiper-wrapper": { gap: initialized ? 0 : "24px" }
          }}
        >
          <Box className="swiper-wrapper">
            {data.map(item => (
              <Box className="swiper-slide" key={item.id}>
                <ProductCard product={item} />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          justifyContent: "space-between",
          width: {
            xs: "calc(100% + 36px)",
            lg: "calc(100% + 116px)"
          },
          zIndex: 10
        }}
      >
        <ButtonNavigation
          disabled={!initialized || isBeginning}
          onClick={handlePrev}
        >
          <Icons name="caret-left" />
        </ButtonNavigation>
        <ButtonNavigation disabled={!initialized || isEnd} onClick={handleNext}>
          <Icons name="caret-right" />
        </ButtonNavigation>
      </Box>
    </Box>
  )
}

export default CarouselProductSale
